import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Lock } from 'lucide-react';

const ProtectedRoute = ({ children, requireAuth = true }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  console.log('ProtectedRoute state:', { isAuthenticated, loading, user: !!user, requireAuth, pathname: location.pathname }); // Debug log

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-slate-300 mb-2">Checking authentication...</div>
          <div className="text-slate-500 text-sm">Please wait while we verify your session</div>
        </div>
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden flex items-center justify-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 text-center max-w-md mx-auto p-8">
          <div className="relative group mb-8">
            {/* Glow Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600/50 to-orange-600/50 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            
            {/* Icon Container */}
            <div className="relative bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-full p-6 inline-block">
              <Shield className="w-12 h-12 text-red-400" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-slate-100 mb-4">
            Authentication Required
          </h1>
          <p className="text-slate-400 mb-8">
            You need to be logged in to access this page. Please sign in to continue.
          </p>
          
          <div className="space-y-4">
            <Navigate 
              to="/login" 
              state={{ from: location }} 
              replace 
            />
            
            <div className="flex items-center justify-center gap-4">
              <a
                href="/login"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-xl transition duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <Lock className="w-4 h-4" />
                Sign In
              </a>
              
              <a
                href="/register"
                className="inline-flex items-center gap-2 px-6 py-3 border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-slate-100 font-medium rounded-xl transition duration-200"
              >
                Create Account
              </a>
            </div>
          </div>
          
          <div className="mt-8 text-xs text-slate-500">
            Redirected from: {location.pathname}
          </div>
        </div>
      </div>
    );
  }

  // If authentication is NOT required but user IS authenticated (for login/register pages)
  if (!requireAuth && isAuthenticated) {
    return <Navigate to="/proposal-outline" replace />;
  }

  // Render the protected content
  return children;
};

// HOC wrapper for easier use
export const withProtection = (Component, options = {}) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

// Role-based protection component
export const RoleProtectedRoute = ({ children, requiredRoles = [], user, fallback }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-slate-300">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required roles
  const userRoles = user?.roles || [];
  const hasRequiredRole = requiredRoles.length === 0 || 
    requiredRoles.some(role => userRoles.includes(role));

  if (!hasRequiredRole) {
    return fallback || (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-100 mb-2">Access Denied</h1>
          <p className="text-slate-400">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;